import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { parseUnits, parseEventId, lifetimeClaimPath, startLifetimeCheckout } from "@/lib/marketing/syre-offer";
import { stashReturnTo } from "@/lib/auth-return-to";
import { resetSessionCache } from "@/lib/queryClient";

/** Dedicated payment continuation: never replace a failed purchase with onboarding. */
export default function LifetimeClaim() {
  const params = new URLSearchParams(window.location.search);
  const units = parseUnits(params.get("units"));
  const eventId = parseEventId(params.get("eventId"));
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled") === "1";
  const [message, setMessage] = useState(canceled ? "Payment was canceled. Your selection is saved; no lifetime access has been granted for this attempt." : "Checking your payment…");
  const [signedIn, setSignedIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activated, setActivated] = useState(false);
  const [canRetry, setCanRetry] = useState(!sessionId);
  const started = useRef(false);
  async function chooseTrial() {
    setBusy(true);
    try {
      const res = await fetch("/api/billing/lifetime-trial-choice", { method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ chooseFreeTrial: true }) });
      const data = await res.json();
      if (!res.ok) { setMessage(data.message ?? "Could not start a separate trial."); setBusy(false); return; }
      resetSessionCache();
      window.location.assign("/app/connect-padsplit");
    } catch { setMessage("Could not start a separate trial. Your lifetime selection is saved."); setBusy(false); }
  }

  async function checkout() {
    setBusy(true);
    const result = await startLifetimeCheckout(units, eventId);
    if (result.ok) { window.location.assign(result.url); return; }
    setBusy(false);
    if (result.reason === "sign_in") {
      stashReturnTo(lifetimeClaimPath(units, eventId));
      setSignedIn(false);
      setMessage("Sign in to continue your selected lifetime purchase.");
      return;
    }
    setMessage(result.reason === "closed"
      ? "This event has closed. Your selection is saved. Contact travis@padpulse.io for help; starting a free trial is a separate choice."
      : result.reason === "over_cap" || result.reason === "already_lifetime"
        ? "This selection exceeds your account’s 20-property lifetime cap. Return to the offer to choose fewer properties."
        : result.reason === "unavailable"
          ? "Checkout is not available for this account. Your selection is saved. Contact travis@padpulse.io for help."
          : `Checkout could not start. Your selection is saved. Retry or contact travis@padpulse.io.${result.reference ? ` Reference: ${result.reference}` : ""}`);
  }

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    async function check() {
      try {
        const auth = await fetch("/api/app/auth/me", { credentials: "include" });
        if (stopped) return;
        if (!auth.ok) {
          stashReturnTo(`${lifetimeClaimPath(units, eventId)}${sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : ""}`);
          setMessage("Sign in to continue your selected lifetime purchase.");
          return;
        }
        setSignedIn(true);
        if (sessionId) {
          const res = await fetch(`/api/billing/lifetime-status?session_id=${encodeURIComponent(sessionId)}`, { credentials: "include" });
          const data = await res.json();
          if (stopped) return;
          if (!res.ok) { setMessage("We could not verify this payment for your account. Contact travis@padpulse.io or retry verification."); return; }
          if (data.status === "active") {
            resetSessionCache();
            setActivated(true);
            setMessage(`Payment confirmed. Your lifetime purchase added ${data.allowance} properties. Continue to setup.`);
          } else if (data.status === "review") {
            setMessage("Your payment was received but needs manual review. Do not pay again. Contact travis@padpulse.io.");
          } else if (data.status === "processing") {
            setMessage("Your payment is processing. We have not confirmed activation yet. This page will check again.");
            timer = setTimeout(check, 3000);
          } else {
            setCanRetry(true);
            setMessage("This payment is unpaid, failed, or refunded. No activation has been confirmed. You can retry checkout.");
          }
        } else if (!canceled && !started.current) {
          started.current = true;
          await checkout();
        }
      } catch { if (!stopped) setMessage("We could not verify payment. Refresh to retry; your selection is saved."); }
    }
    void check();
    return () => { stopped = true; clearTimeout(timer); };
  }, [units, sessionId, canceled, eventId]);

  return <div className="flex min-h-screen flex-col bg-white">
    <PublicHeader />
    <main className="mx-auto w-full max-w-xl flex-grow px-5 pb-16 pt-32">
      <h1 className="text-3xl font-bold">Your lifetime purchase</h1>
      <p className="mt-4">{units * 5} properties · ${(units * 3000).toLocaleString()} one-time payment</p>
      <p role="status" className="my-6">{message}</p>
      <div className="flex flex-col gap-4">
        {activated ? <Link href="/app/connect-padsplit"><Button>Continue to setup</Button></Link>
          : signedIn ? canRetry
            ? <Button disabled={busy} onClick={() => void checkout()}>{busy ? "Opening checkout…" : "Retry selected checkout"}</Button>
            : <Button variant="outline" onClick={() => window.location.reload()}>Check payment again</Button>
            : <Link href="/app/login"><Button>Sign in and continue</Button></Link>}
        <Link href={`/syre?units=${units}${eventId ? `&eventId=${eventId}` : ""}`}>Return to selected offer</Link>
        <a href="mailto:travis@padpulse.io">Email Travis for help</a>
        {!activated && canRetry && (signedIn
          ? <Button className="h-auto min-h-10 w-full whitespace-normal px-4 py-3 text-center" variant="outline" disabled={busy} onClick={() => void chooseTrial()}>Choose a separate 14-day free trial instead (no card needed)</Button>
          : <Link href="/signup">Choose a separate 14-day free trial instead (no card needed)</Link>)}
      </div>
    </main>
    <PublicFooter />
  </div>;
}
